import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Interview {
    id: string;
    tenantId: string;
    candidateId: string;
    candidateFirstName?: string;
    candidateLastName?: string;
    candidateRole?: string;
    interviewerId?: string;
    interviewerFirstName?: string;
    interviewerLastName?: string;
    type: string;
    scheduledTime: string;
    durationMinutes: number;
    status: string;
    feedback?: string;
    createdAt: string;
    updatedAt: string;
}

interface InterviewState {
    interviews: Interview[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchInterviews: (filters?: any) => Promise<void>;
    createInterview: (data: Partial<Interview>) => Promise<Interview>;
    updateInterview: (id: string, data: Partial<Interview>) => Promise<Interview>;
    deleteInterview: (id: string) => Promise<void>;
}

export const useInterviewStore = create<InterviewState>((set, get) => ({
    interviews: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchInterviews: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/interviews', { params: filters });
            const data = response.data?.data ?? response.data;
            const pagination = response.data?.pagination ?? { page: 1, limit: 20, total: Array.isArray(data) ? data.length : 0, totalPages: 1 };
            set({ interviews: Array.isArray(data) ? data : [], pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch interviews', isLoading: false });
        }
    },
    createInterview: async (data: Partial<Interview>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/interviews', data);
            const newInterview = response.data?.data ?? response.data;
            set((state) => ({
                interviews: [newInterview, ...state.interviews],
                isLoading: false,
            }));
            return newInterview;
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create interview', isLoading: false });
            throw error;
        }
    },
    updateInterview: async (id: string, data: Partial<Interview>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/interviews/${id}`, data);
            const updatedInterview = response.data?.data ?? response.data;
            set((state) => ({
                interviews: state.interviews.map((o) => (o.id === id ? updatedInterview : o)),
                isLoading: false,
            }));
            return updatedInterview;
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update interview', isLoading: false });
            throw error;
        }
    },
    deleteInterview: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
            await apiClient.delete(`/people/interviews/${id}`);
            set((state) => ({
                interviews: state.interviews.filter((o) => o.id !== id),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete interview', isLoading: false });
            throw error;
        }
    },
}));
